<template>
  <div>
    <svg viewBox="0 0 400 330" style="width:100%; max-width:400px; display:block; margin:0 auto">

      <defs>
        <marker id="arrow-val-a" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M 0 0 L 7 3.5 L 0 7 Z" fill="#1565C0" />
        </marker>
        <marker id="arrow-val-b" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M 0 0 L 7 3.5 L 0 7 Z" fill="#2E7D32" />
        </marker>
      </defs>

      <!-- Higher-order value zones — asymmetric sectors matching actual circumplex coverage -->
      <!-- SE: Power(0°)+Achievement(36°) → 342°–54°  (72°) -->
      <!-- OtC: Hedonism(72°)+Stimulation(108°)+Self-direction(144°) → 54°–162° (108°) -->
      <!-- ST: Universalism(180°)+Benevolence(216°) → 162°–234° (72°)  -->
      <!-- Con: Tradition(252°)+Conformity(288°)+Security(324°) → 234°–342° (108°) -->
      <path v-for="z in ZONES" :key="z.key"
            :d="zonePath(z.startRad, z.endRad)"
            :fill="z.fill"
            stroke="none" />

      <!-- Outer circle border -->
      <circle :cx="cx" :cy="cy" :r="circleR"
              fill="none" stroke="#ccc" stroke-width="1.5" />

      <!-- Zone labels at centroid of each sector -->
      <text v-for="z in ZONES" :key="'lbl-' + z.key"
            :x="cx + zoneLabelR * Math.cos(z.centroidRad)"
            :y="cy - zoneLabelR * Math.sin(z.centroidRad)"
            text-anchor="middle" dominant-baseline="middle"
            font-size="9.5" :fill="z.textColor" font-weight="600"
            opacity="0.5">{{ z.label }}</text>

      <!-- Arc showing the angle between the two vectors -->
      <path v-if="vecAngleA != null && vecAngleB != null"
            :d="sectorPath"
            fill="none"
            stroke="#e53935"
            stroke-width="2"
            stroke-dasharray="5 4"
            stroke-linecap="round" />

      <!-- Phi angle label -->
      <text v-if="phiDeg != null"
            :x="phiLabelX" :y="phiLabelY"
            text-anchor="middle" dominant-baseline="middle"
            font-size="13" fill="#e53935" font-weight="bold">{{ phiDeg }}°</text>

      <!-- Vector A arrow -->
      <line v-if="vecAngleA != null"
            :x1="cx" :y1="cy"
            :x2="arrowX(vecAngleA)" :y2="arrowY(vecAngleA)"
            stroke="#1565C0" stroke-width="2.5"
            marker-end="url(#arrow-val-a)" />

      <!-- Vector B arrow -->
      <line v-if="vecAngleB != null"
            :x1="cx" :y1="cy"
            :x2="arrowX(vecAngleB)" :y2="arrowY(vecAngleB)"
            stroke="#2E7D32" stroke-width="2.5"
            marker-end="url(#arrow-val-b)" />

      <!-- Center dot -->
      <circle :cx="cx" :cy="cy" r="3" fill="#9e9e9e" />

    </svg>

    <!-- Arrows legend -->
    <div class="d-flex justify-center mt-1" style="gap:24px">
      <div class="d-flex align-center" style="gap:6px">
        <div style="width:18px;height:3px;background:#1565C0;border-radius:2px" />
        <span class="text-caption">{{ labelA }}</span>
      </div>
      <div class="d-flex align-center" style="gap:6px">
        <div style="width:18px;height:3px;background:#2E7D32;border-radius:2px" />
        <span class="text-caption">{{ labelB }}</span>
      </div>
    </div>

    <!-- Zones description list -->
    <ul class="zones-list mt-3">
      <li v-for="z in ZONES" :key="'leg-' + z.key">
        <span class="zone-title" :style="`color:${z.textColor}`">{{ z.label }}</span>
        — {{ z.desc }}
      </li>
    </ul>
  </div>
</template>

<script>
const DEG = Math.PI / 180

// Zone boundaries align with value positions on the circumplex (PVQ21_SCORING.md §4):
// Circumplex order (CCW, 36° step): Benevolence(0°) Universalism(36°) Self-direction(72°)
//   Stimulation(108°) Hedonism(144°) Achievement(180°) Power(216°) Security(252°) Tradition(288°) Conformity(324°)
// ST:  0° →  72°  (Benevolence + Universalism)
// OtC: 72° → 180° (Self-direction + Stimulation + Hedonism)
// SE: 180° → 252° (Achievement + Power)
// Con: 252° → 360° (Security + Tradition + Conformity)
const ZONES = [
  {
    key:         'st',
    label:       'Transcendență',
    desc:        'Binele celorlalți și al lumii primează. Empatia, grija față de oameni și dreptatea socială sunt valori centrale.',
    startRad:      0 * DEG,
    endRad:       72 * DEG,
    centroidRad:  36 * DEG,   // midpoint: Universalism(36°)
    fill:        'rgba(76, 175, 80, 0.13)',
    textColor:   '#1B5E20',
  },
  {
    key:         'otc',
    label:       'Deschidere',
    desc:        'Noutatea, autonomia și experiențele stimulante sunt prioritare. Libertatea de a gândi și acționa independent e esențială.',
    startRad:    72 * DEG,
    endRad:      180 * DEG,
    centroidRad: 126 * DEG,   // midpoint of Self-direction(72°) … Hedonism(144°)
    fill:        'rgba(33, 150, 243, 0.11)',
    textColor:   '#0D47A1',
  },
  {
    key:         'se',
    label:       'Autopromovare',
    desc:        'Succesul personal, statutul social și influența asupra celorlalți contează mult. Ambiția și recunoașterea sunt motoare importante.',
    startRad:    180 * DEG,
    endRad:      252 * DEG,
    centroidRad: 216 * DEG,   // midpoint: Power(216°)
    fill:        'rgba(211, 47, 47, 0.13)',
    textColor:   '#B71C1C',
  },
  {
    key:         'con',
    label:       'Conservare',
    desc:        'Stabilitatea, tradiția și ordinea socială oferă siguranță. Respectul față de norme și continuitate sunt importante.',
    startRad:    252 * DEG,
    endRad:      360 * DEG,
    centroidRad: 306 * DEG,   // midpoint of Security(252°) … Conformity(324°)
    fill:        'rgba(156, 39, 176, 0.10)',
    textColor:   '#4A148C',
  },
]

export default {
  name: 'ValuesVectorChart',

  props: {
    vecAngleA: { type: Number, default: null },  // radians — direction of person A's resultant vector
    vecAngleB: { type: Number, default: null },  // radians — direction of person B's resultant vector
    phi:       { type: Number, default: null },  // radians — angle between the two vectors
    labelA:    { type: String, default: 'Tu' },
    labelB:    { type: String, default: 'Ei' },
  },

  data() {
    return {
      ZONES,
      cx:          200,
      cy:          165,
      circleR:     150,  // background circle radius
      arrowR:      134,  // arrow endpoint radius
      zoneLabelR:  98,   // zone label radius (inside the circle)
      sectorR:     58,   // radius of the phi sector arc
    }
  },

  computed: {
    phiDeg() {
      return this.phi != null ? Math.round(this.phi * 180 / Math.PI) : null
    },

    // Bisector angle between the two vectors (for placing the phi label)
    midAngle() {
      let diff = this.vecAngleB - this.vecAngleA
      while (diff >  Math.PI) diff -= 2 * Math.PI
      while (diff < -Math.PI) diff += 2 * Math.PI
      return this.vecAngleA + diff / 2
    },

    phiLabelX() {
      return this.cx + (this.sectorR + 8) * Math.cos(this.midAngle)
    },

    phiLabelY() {
      return this.cy - (this.sectorR + 8) * Math.sin(this.midAngle)
    },

    // Arc path drawn as many short segments to avoid SVG arc sweep ambiguity
    sectorPath() {
      const steps = 32
      let diff = this.vecAngleB - this.vecAngleA
      while (diff >  Math.PI) diff -= 2 * Math.PI
      while (diff < -Math.PI) diff += 2 * Math.PI

      const pts = []
      for (let i = 0; i <= steps; i++) {
        const a = this.vecAngleA + (diff * i) / steps
        pts.push(`${this.cx + this.sectorR * Math.cos(a)},${this.cy - this.sectorR * Math.sin(a)}`)
      }
      return `M ${pts[0]} L ${pts.slice(1).join(' L ')}`
    },
  },

  methods: {
    // Zone sector path — filled sector from center to circle edge
    zonePath(startRad, endRad) {
      const steps = 32
      const span  = endRad - startRad
      const pts   = []
      for (let i = 0; i <= steps; i++) {
        const a = startRad + (span * i) / steps
        pts.push(`${this.cx + this.circleR * Math.cos(a)},${this.cy - this.circleR * Math.sin(a)}`)
      }
      return `M ${this.cx},${this.cy} L ${pts.join(' L ')} Z`
    },

    arrowX(angle) {
      return this.cx + this.arrowR * Math.cos(angle)
    },

    arrowY(angle) {
      return this.cy - this.arrowR * Math.sin(angle)
    },
  },
}
</script>

<style scoped>
.zones-list {
  padding-left: 1.1rem;
  margin: 0;
}
.zones-list li {
  font-size: 0.78rem;
  color: #555;
  margin-bottom: 4px;
  line-height: 1.4;
}
.zone-title {
  font-weight: 600;
}
</style>